import { useCallback, useEffect, useRef } from 'react'
import { useParams, useRouter, useSearch } from '@tanstack/react-router'
import { useRoomStore, useRoomStoreActions } from '@/stores/room'
import { logger } from '@/logger/createLogger'
import { AudioCallControls } from '@/components/AudioCallControls'
import { CardLoading } from '@/components/CardLoading'
import { Feature } from '@/components/Feature'
import { MemberOverlays } from '@/components/MemberOverlays'
import { RoomSessionTimer } from '@/components/RoomSessionTimer'
import { ENV_VARS, ROOT_ELEMENT_ID_MCU, TEST_IDS } from '@/helpers/constants'
import { FEATURES } from '@/stores/features'
import type { CallError, ChannelType, RedirectPath } from '@/helpers/types'
import { cn } from '@/helpers/utils'
import type { RoomContext } from '@/helpers/room'

interface PathParams {
  context: RoomContext
  name: string
}
interface SearchParams {
  channel?: ChannelType
  redirect: RedirectPath
}

export const RoomView = () => {
  const rootMcuRef = useRef<HTMLDivElement>(null)

  // TODO: fix typecasting caused by the C2C router not using the same path
  const { context, name } = useParams({ strict: false }) as PathParams
  const { channel, redirect } = useSearch({ strict: false }) as SearchParams

  const initialized = useRef<boolean>(false)

  const callState = useRoomStore(state => state.callState)
  const mediaConnected = useRoomStore(state => state.mediaConnected)
  const isAudioOnly = useRoomStore(state => state.isAudioOnly)

  const { joinRoom, setCallError } = useRoomStoreActions()

  const router = useRouter()

  const navigateWithCallErrorReason = useCallback(
    (reason: CallError) => {
      setCallError(reason)
      void router.navigate({
        to: redirect || ENV_VARS.DEFAULT_NAV_ROUTE,
      })
    },
    [setCallError, redirect, router],
  )

  const joinThisRoom = useCallback(
    async (context: RoomContext) => {
      // ensure the root element is available before joining the room
      if (rootMcuRef.current === null) {
        return
      }

      await joinRoom({
        channel: channel,
        context: context,
        name: name,
        redirect: redirect,
        rootElement: rootMcuRef.current,
      }).catch(error => {
        logger.error('Error joining room', error)
        navigateWithCallErrorReason({ type: 'dial' })
      })
    },
    [channel, joinRoom, name, navigateWithCallErrorReason, redirect],
  )

  const viewElementRef = useRef(null)

  useEffect(() => {
    // Run only once so that the room is not rejoined
    if (initialized.current || !rootMcuRef.current || callState !== 'ready') {
      return
    }
    initialized.current = true

    // TODO: the router schema should prevent this from happening and reject loading the view
    // maybe it can be handled in the beforeLoad or loader functions
    if (!context || !name) {
      logger.error('A parameter was missing when entering the room')

      const missingParam = !context ? 'context' : 'name'
      navigateWithCallErrorReason({
        message: missingParam,
        type: 'prejoin',
      })

      return
    }

    void joinThisRoom(context)
  }, [
    context,
    joinThisRoom,
    callState,
    name,
    redirect,
    navigateWithCallErrorReason,
  ])

  const handleCancel = async () => {
    // FIXME: leave and ensure the room is cleaned up and the room session is ended
  }

  // TODO: is this still necessary? or can it be removed?,
  // maybe it can be handled in the beforeLoad or loader functions
  if (!context?.trim() || !name?.trim()) {
    // TODO: error message displayed in video area?
    return null
  }

  return (
    <>
      <Feature name={FEATURES.ROOM_LOADING_UI}>
        {!mediaConnected && (
          // NOTE: translate the loading card in the same position as router's pendingComponent to avoid layout shift
          <div
            className="absolute left-0 top-0 z-10 flex h-full w-full -translate-y-7 flex-col items-center justify-center overflow-hidden"
            data-testid={TEST_IDS.ROOM_LOADING_CARD}
          >
            <CardLoading
              cardTitle="Connecting"
              cardDescription={`Joining ${context}/${name}`}
              // TODO: add cancel label after implementing handleCancel
              confirmLabel=""
              onConfirm={handleCancel}
              open
            />
          </div>
        )}
      </Feature>

      {isAudioOnly && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-y-20 bg-primary">
          {mediaConnected && <RoomSessionTimer startTimer />}
          {callState === 'joined' ? (
            <AudioCallControls redirect={redirect} />
          ) : (
            <div className="flex min-h-[368px] w-full flex-col"></div>
          )}
        </div>
      )}

      <div
        className={cn(
          'flex h-full flex-col overflow-hidden',
          (isAudioOnly || !mediaConnected) && 'hidden',
        )}
      >
        {!isAudioOnly && mediaConnected && (
          <RoomSessionTimer
            className="gradient flex w-full shrink-0 justify-center text-sm font-medium"
            startTimer
          />
        )}
        <div className="h-full overflow-hidden" ref={viewElementRef}>
          <MemberOverlays />
          <div id={ROOT_ELEMENT_ID_MCU} ref={rootMcuRef} />
        </div>
      </div>
    </>
  )
}
